import React, { useState, useEffect, useRef } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { useAuth } from '../../context/AuthContext';
import { WhatsAppSocketProvider, useWhatsAppSocket } from '../../context/WhatsAppSocketContext';
import { 
  Settings, Send, Check, CheckCheck, ShieldAlert, KeyRound, Phone, 
  User as UserIcon, MessageCircle, Trash2, UserCheck, RefreshCw, 
  History, Clock, ArrowRightLeft, UserX, CheckSquare
} from 'lucide-react';
import QuickReplies from '../../components/whatsapp/QuickReplies';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const WhatsAppPageContent = () => {
  const { user } = useAuth();
  const { socket, isConnected: socketConnected } = useWhatsAppSocket();
  
  const [config, setConfig] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [activeContact, setActiveContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [logs, setLogs] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  const [activeTab, setActiveTab] = useState('unassigned'); // 'unassigned' | 'mine' | 'all'
  const [newMessageText, setNewMessageText] = useState('');
  
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  // Settings Form state
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [verifyToken, setVerifyToken] = useState('crmautomax_verify_secret_token_2026');
  const [displayName, setDisplayName] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);

  const messagesEndRef = useRef(null);

  // 1. Fetch Config
  const fetchConfig = async () => {
    try {
      setLoadingConfig(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        if (data.isConnected) {
          setPhoneNumberId(data.phoneNumberId || '');
          setDisplayName(data.displayName || '');
          setWabaId(data.wabaId || '');
          setVerifyToken(data.verifyToken || 'crmautomax_verify_secret_token_2026');
          fetchContacts();
          fetchEmployees();
        } else {
          setShowSettingsModal(true);
        }
      }
    } catch (err) {
      console.error('Error fetching WhatsApp config:', err);
    } finally {
      setLoadingConfig(false);
    }
  };

  // 2. Fetch Contacts based on activeTab
  const fetchContacts = async () => {
    try {
      setLoadingContacts(true);
      const token = localStorage.getItem('crm_token');
      let endpoint = '/whatsapp/contacts/unassigned';
      if (activeTab === 'mine') endpoint = '/whatsapp/contacts/mine';
      if (activeTab === 'all') endpoint = '/whatsapp/contacts/all';

      const res = await fetch(`${API_BASE}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setContacts(data);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  };

  // 3. Fetch Employees for reassignment dropdown
  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEmployees(data.filter(u => u.role !== 'master-admin'));
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  // 4. Fetch Conversation messages
  const fetchMessages = async (phone) => {
    try {
      setLoadingMessages(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/messages/${phone}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
        
        // Zero out unread count in UI contact list instantly
        setContacts(prev =>
          prev.map(c => (c.phone === phone ? { ...c, unreadCount: 0 } : c))
        );
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  // 5. Fetch Activity Logs
  const fetchLogs = async (contactId) => {
    try {
      setLoadingLogs(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/contacts/${contactId}/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data);
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
    } finally {
      setLoadingLogs(false);
    }
  };

  // 6. Accept unassigned chat
  const handleAcceptChat = async (contactId) => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/contacts/${contactId}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        fetchContacts();
        if (activeContact && activeContact._id === contactId) {
          setActiveContact(data.contact);
          fetchLogs(contactId);
        }
      }
    } catch (err) {
      console.error('Error accepting chat:', err);
    }
  };

  // 7. Assign / Re-assign chat to employee
  const handleAssignChat = async (contactId, targetUserId) => {
    if (!targetUserId) return;
    try {
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/contacts/${contactId}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ targetUserId })
      });
      if (res.ok) {
        const data = await res.json();
        fetchContacts();
        if (activeContact && activeContact._id === contactId) {
          setActiveContact(data.contact);
          fetchLogs(contactId);
        }
      } else {
        const errData = await res.json();
        alert(errData.message || 'Failed to assign chat');
      }
    } catch (err) {
      console.error('Error assigning chat:', err);
    }
  };

  // 8. Permanent delete chat
  const handleDeleteChat = async (contactId) => {
    if (!window.confirm('Are you sure you want to permanently delete this chat? All messages and activity logs will be deleted.')) return;
    try {
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/contacts/${contactId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setActiveContact(null);
        setMessages([]);
        setLogs([]);
        fetchContacts();
      } else {
        const errData = await res.json();
        alert(errData.message || 'Failed to delete chat');
      }
    } catch (err) {
      console.error('Error deleting chat:', err);
    }
  };

  // 9. Save Config
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    if (!phoneNumberId || !accessToken || !verifyToken) {
      setSettingsError('Please fill in Phone Number ID, Access Token, and Verification Token.');
      return;
    }

    try {
      setSettingsSaving(true);
      setSettingsError('');
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          phoneNumberId,
          accessToken,
          verifyToken,
          displayName: displayName || 'WhatsApp Business API',
          wabaId
        })
      });

      const data = await res.json();
      if (res.ok) {
        setConfig(data.config);
        setShowSettingsModal(false);
        fetchContacts();
      } else {
        setSettingsError(data.message || 'Failed to save configuration');
      }
    } catch (err) {
      setSettingsError('Server connection failed. Try again.');
      console.error(err);
    } finally {
      setSettingsSaving(false);
    }
  };

  // 10. Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessageText.trim() || !activeContact || sendingMessage) return;

    const to = activeContact.phone;
    const body = newMessageText.trim();

    try {
      setSendingMessage(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ to, message: body })
      });

      if (res.ok) {
        setNewMessageText('');
        if (activeContact) {
          fetchLogs(activeContact._id);
        }
      } else {
        const data = await res.json();
        alert(data.message || 'Failed to send message via Meta Cloud API.');
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  // Disconnect handler
  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect this WhatsApp Business integration?')) return;
    try {
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/config`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setConfig(null);
        setContacts([]);
        setActiveContact(null);
        setMessages([]);
        setLogs([]);
        setAccessToken('');
        setPhoneNumberId('');
        setShowSettingsModal(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Auto Scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initial load
  useEffect(() => {
    fetchConfig();
  }, []);

  // Fetch contacts when tab changes
  useEffect(() => {
    fetchContacts();
  }, [activeTab]);

  // Fetch thread when contact switches
  useEffect(() => {
    if (activeContact) {
      fetchMessages(activeContact.phone);
      fetchLogs(activeContact._id);
    }
  }, [activeContact]);

  // Real-time socket event subscription
  useEffect(() => {
    if (!socket) return;

    const handleNewMsg = ({ contact, message }) => {
      if (activeContact && activeContact.phone === message.contactPhone) {
        setMessages(prev => {
          if (message.wamid && prev.some(m => m.wamid === message.wamid)) return prev;
          return [...prev, message];
        });
        fetch(`${API_BASE}/whatsapp/messages/${activeContact.phone}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` }
        });
        fetchLogs(activeContact._id);
      }
      fetchContacts();
    };

    const handleStatusUpdate = ({ contactPhone, wamid, status }) => {
      if (activeContact && activeContact.phone === contactPhone) {
        setMessages(prev =>
          prev.map(m => (m.wamid === wamid ? { ...m, status } : m))
        );
      }
    };

    const handleRead = ({ phone }) => {
      setContacts(prev =>
        prev.map(c => (c.phone === phone ? { ...c, unreadCount: 0 } : c))
      );
    };

    const handleChatAssignedEvent = () => {
      fetchContacts();
      if (activeContact) {
        fetchLogs(activeContact._id);
      }
    };

    const handleChatDeletedEvent = ({ contactId }) => {
      if (activeContact && activeContact._id === contactId) {
        setActiveContact(null);
        setMessages([]);
        setLogs([]);
      }
      fetchContacts();
    };

    socket.on('new_message', handleNewMsg);
    socket.on('message_status_update', handleStatusUpdate);
    socket.on('contact_read', handleRead);
    socket.on('chat_assigned', handleChatAssignedEvent);
    socket.on('chat_auto_assigned', handleChatAssignedEvent);
    socket.on('chat_reassigned', handleChatAssignedEvent);
    socket.on('chat_deleted', handleChatDeletedEvent);

    return () => {
      socket.off('new_message', handleNewMsg);
      socket.off('message_status_update', handleStatusUpdate);
      socket.off('contact_read', handleRead);
      socket.off('chat_assigned', handleChatAssignedEvent);
      socket.off('chat_auto_assigned', handleChatAssignedEvent);
      socket.off('chat_reassigned', handleChatAssignedEvent);
      socket.off('chat_deleted', handleChatDeletedEvent);
    };
  }, [socket, activeContact]);

  // Helper render status ticks
  const renderMsgStatus = (status) => {
    switch (status) {
      case 'read':
        return <CheckCheck size={16} color="#34B7F1" />;
      case 'delivered':
        return <CheckCheck size={16} color="var(--color-text-tertiary)" />;
      case 'sent':
        return <Check size={16} color="var(--color-text-tertiary)" />;
      case 'failed':
        return <ShieldAlert size={16} color="#E24B4A" title="Failed to deliver" />;
      default:
        return null;
    }
  };

  // Helper to format actions in activity timeline logs
  const renderLogActionIcon = (action) => {
    switch (action) {
      case 'chat_received':
        return <MessageCircle size={16} color="#10b981" />;
      case 'auto_assigned':
        return <UserCheck size={16} color="#3b82f6" />;
      case 'manually_accepted':
        return <CheckSquare size={16} color="#8b5cf6" />;
      case 'reassigned':
        return <ArrowRightLeft size={16} color="#f59e0b" />;
      case 'message_sent':
        return <Send size={14} color="#64748b" />;
      case 'message_received':
        return <Clock size={14} color="#10b981" />;
      case 'chat_deleted':
        return <Trash2 size={16} color="#ef4444" />;
      default:
        return <History size={16} color="#64748b" />;
    }
  };

  const getWebhookURL = () => {
    const origin = window.location.origin;
    if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
      return `https://[YOUR_NGROK_TUNNEL_URL]/api/whatsapp/webhook`;
    }
    return `${API_BASE}/whatsapp/webhook`;
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden', background: '#f5f7fb' }}>
      
      {/* ── LEFT PANEL: TAB SWITCHER & CONTACTS ────────────────────────────────── */}
      <div style={{
        width: '340px',
        background: 'white',
        borderRight: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header Section */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1e293b' }}>WhatsApp Inbox</h3>
            {config?.isConnected && (
              <span style={{ fontSize: '12px', color: socketConnected ? '#22c55e' : '#f59e0b', fontWeight: '500' }}>
                ● {socketConnected ? 'Live Connection' : 'Connecting...'}
              </span>
            )}
          </div>
          <button 
            onClick={() => setShowSettingsModal(true)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#64748b',
              padding: '6px',
              borderRadius: '6px',
              transition: 'background 0.2s'
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            <Settings size={20} />
          </button>
        </div>

        {/* Custom Tab Bar */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e2e8f0',
          background: '#f8fafc',
          padding: '4px'
        }}>
          {['unassigned', 'mine', 'all'].map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setActiveContact(null);
              }}
              style={{
                flex: 1,
                padding: '8px 4px',
                fontSize: '13px',
                fontWeight: activeTab === tab ? '600' : '500',
                color: activeTab === tab ? '#22c55e' : '#64748b',
                background: activeTab === tab ? 'white' : 'transparent',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                boxShadow: activeTab === tab ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              {tab === 'unassigned' && 'Unassigned'}
              {tab === 'mine' && 'My Chats'}
              {tab === 'all' && 'All Chats'}
            </button>
          ))}
        </div>

        {/* Contacts Scroller */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loadingContacts && contacts.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>Loading conversations...</div>
          ) : contacts.length === 0 ? (
            <div style={{ padding: '40px 24px', textAlign: 'center', color: '#94a3b8' }}>
              <MessageCircle size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <div>No chats in this queue</div>
              <div style={{ fontSize: '11px', marginTop: '6px' }}>Incoming WhatsApp messages will appear here.</div>
            </div>
          ) : (
            contacts.map((c) => {
              const isActive = activeContact && activeContact.phone === c.phone;
              return (
                <div
                  key={c._id || c.phone}
                  onClick={() => setActiveContact(c)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    background: isActive ? '#f0fdf4' : 'transparent',
                    borderBottom: '1px solid #f1f5f9',
                    borderLeft: isActive ? '3px solid #22c55e' : '3px solid transparent',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => {
                    if (!isActive) e.currentTarget.style.background = '#f8fafc';
                  }}
                  onMouseLeave={e => {
                    if (!isActive) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: '#e2fbe8',
                      color: '#15803d',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      marginRight: '12px'
                    }}>
                      {c.name ? c.name[0].toUpperCase() : <UserIcon size={16} />}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, fontSize: '14px', color: '#334155', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                          {c.name || `+${c.phone}`}
                        </span>
                        <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                          {c.lastMessageAt ? new Date(c.lastMessageAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                        <span style={{ fontSize: '12px', color: '#64748b', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '150px' }}>
                          {c.lastMessagePreview || 'Click to view chat'}
                        </span>
                        {c.unreadCount > 0 && (
                          <div style={{
                            background: '#ef4444',
                            color: 'white',
                            borderRadius: '12px',
                            minWidth: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            padding: '0 6px'
                          }}>
                            {c.unreadCount}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {activeTab === 'unassigned' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAcceptChat(c._id);
                        }}
                        style={{
                          background: '#22c55e',
                          color: 'white',
                          border: 'none',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          marginLeft: '52px'
                        }}
                      >
                        Accept Chat
                      </button>
                    )}
                    {c.assignedToName && (
                      <span style={{
                        fontSize: '11px',
                        color: '#64748b',
                        background: '#f1f5f9',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        marginLeft: '52px',
                        fontWeight: '500'
                      }}>
                        👤 {c.assignedToName}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── CENTER PANEL: CHAT WINDOW ─────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#eae6df' }}>
        {activeContact ? (
          <>
            {/* Chat header */}
            <div style={{
              padding: '12px 24px',
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: '#e2fbe8',
                  color: '#15803d',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  marginRight: '12px'
                }}>
                  {activeContact.name ? activeContact.name[0].toUpperCase() : <UserIcon size={16} />}
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '15px', color: '#1e293b' }}>{activeContact.name || 'WhatsApp Contact'}</h4>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>+{activeContact.phone}</span>
                </div>
              </div>

              {/* Assignment Actions & Delete Button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {(!activeContact.assignedTo || activeContact.status === 'unassigned') ? (
                  <button
                    onClick={() => handleAcceptChat(activeContact._id)}
                    style={{
                      background: '#22c55e',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <UserCheck size={16} /> Accept Chat
                  </button>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>Assigned:</span>
                    <select
                      value={activeContact.assignedTo || ''}
                      onChange={(e) => handleAssignChat(activeContact._id, e.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        background: 'white',
                        fontSize: '13px',
                        color: '#334155',
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="" disabled>Select User...</option>
                      {employees.map((emp) => (
                        <option key={emp._id} value={emp._id}>
                          {emp.name} ({emp.role})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <button
                  onClick={() => handleDeleteChat(activeContact._id)}
                  style={{
                    background: '#fee2e2',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#ef4444',
                    padding: '8px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s'
                  }}
                  title="Delete Chat Permanently"
                  onMouseEnter={e => e.currentTarget.style.background = '#fca5a5'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fee2e2'}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>

            {/* Messages box */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px',
              backgroundImage: 'radial-gradient(#dfdcd6 10%, transparent 10%)',
              backgroundSize: '20px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              {loadingMessages && messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#64748b', background: '#ffffffcc', padding: '8px 16px', borderRadius: '20px', margin: 'auto' }}>
                  Loading chat...
                </div>
              ) : (
                messages.map((m) => {
                  const isOut = m.direction === 'outbound';
                  return (
                    <div
                      key={m._id || m.wamid}
                      style={{
                          alignSelf: isOut ? 'flex-end' : 'flex-start',
                          background: isOut ? (m.sentByName === 'AI Assistant' ? '#f3e8ff' : '#d9fdd3') : 'white',
                        color: '#303030',
                        padding: '8px 12px 6px',
                        borderRadius: '8px',
                        boxShadow: '0 1px 0.5px rgba(11,20,26,.13)',
                        maxWidth: '60%',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        wordBreak: 'break-word'
                      }}
                    >
                      {/* Sender identification for outbound message */}
                      {isOut && m.sentByName && (
                        <span style={{ fontSize: '9px', fontStyle: 'italic', color: '#858585', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {m.sentByName === 'AI Assistant' && <span style={{fontSize:'12px'}} title="AI Auto-Reply">🤖</span>}
                          sent by {m.sentByName}
                        </span>
                      )}
                      <span style={{ fontSize: '14px', marginRight: '36px' }}>{m.body}</span>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '4px',
                        marginTop: '2px',
                        fontSize: '9px',
                        color: '#707070',
                        alignSelf: 'flex-end'
                      }}>
                        <span>{new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        {isOut && renderMsgStatus(m.status)}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Send bar */}
            <form
              onSubmit={handleSendMessage}
              style={{
                padding: '16px 24px',
                background: '#f0f2f5',
                display: 'flex',
                gap: '12px',
                alignItems: 'center'
              }}
            >
              <QuickReplies onSelect={(text) => setNewMessageText(prev => prev ? `${prev} ${text}` : text)} />
              <input
                type="text"
                value={newMessageText}
                onChange={e => setNewMessageText(e.target.value)}
                placeholder={(!activeContact.assignedTo || activeContact.status === 'unassigned') ? "Accept chat to type a message..." : "Type a message..."}
                disabled={sendingMessage}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  borderRadius: '24px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '14px',
                  boxShadow: '0 1px 1px rgba(0,0,0,0.05)'
                }}
              />
              <button
                type="submit"
                disabled={!newMessageText.trim() || sendingMessage}
                style={{
                  background: '#22c55e',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'white',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(34,197,94,0.3)',
                  transition: 'opacity 0.2s',
                  opacity: (!newMessageText.trim() || sendingMessage) ? 0.6 : 1
                }}
              >
                <Send size={16} style={{ marginLeft: '2px' }} />
              </button>
            </form>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px', color: '#64748b' }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: '#22c55e20',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#22c55e',
              marginBottom: '20px'
            }}>
              <MessageCircle size={48} />
            </div>
            <h3 style={{ margin: 0, color: '#334155' }}>WhatsApp Business Integration</h3>
            <p style={{ fontSize: '14px', marginTop: '6px', color: '#64748b', textAlign: 'center', maxWidth: '360px' }}>
              Select a conversation from the sidebar to view thread, accept claims, assign or delete.
            </p>
          </div>
        )}
      </div>

      {/* ── RIGHT PANEL: ACTIVITY AUDIT LOG ───────────────────────────────────── */}
      {activeContact && (
        <div style={{
          width: '280px',
          background: 'white',
          borderLeft: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <History size={18} color="#64748b" />
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#1e293b' }}>Activity Audit Logs</h3>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            {loadingLogs && logs.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#64748b' }}>Loading logs...</div>
            ) : logs.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', marginTop: '20px' }}>No activities recorded.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', position: 'relative' }}>
                <div style={{
                  position: 'absolute',
                  left: '12px',
                  top: '10px',
                  bottom: '10px',
                  width: '2px',
                  background: '#f1f5f9',
                  zIndex: 0
                }} />

                {logs.map((log) => (
                  <div key={log._id} style={{ display: 'flex', gap: '12px', position: 'relative', zIndex: 1 }}>
                    <div style={{
                      width: '26px',
                      height: '26px',
                      borderRadius: '50%',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {renderLogActionIcon(log.action)}
                    </div>

                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>
                          {log.action === 'chat_received' && 'Chat Initiated'}
                          {log.action === 'auto_assigned' && 'Auto-Assigned'}
                          {log.action === 'manually_accepted' && 'Chat Claimed'}
                          {log.action === 'reassigned' && 'Chat Transferred'}
                          {log.action === 'message_sent' && 'Agent Replied'}
                          {log.action === 'message_received' && 'Client Replied'}
                        </span>
                        <span style={{ fontSize: '9px', color: '#94a3b8' }}>
                          {new Date(log.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' })} {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#64748b', lineHeight: '1.4' }}>
                        {log.note || `${log.performedByName || 'System'} modified chat state.`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── SETTINGS MODAL ─────────────────────────────────────────────────── */}
      {showSettingsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '540px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            padding: '28px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <KeyRound size={20} color="#22c55e" />
                WhatsApp API Setup
              </h3>
              {config?.isConnected && (
                <button
                  onClick={() => setShowSettingsModal(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '18px' }}
                >
                  ✕
                </button>
              )}
            </div>

            <form onSubmit={handleSaveSettings} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                  Business Display Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. My Agency Support"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                  Phone Number ID
                </label>
                <input
                  type="text"
                  placeholder="e.g. 10982341498114"
                  value={phoneNumberId}
                  onChange={e => setPhoneNumberId(e.target.value)}
                  required
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                  Permanent System User Access Token
                </label>
                <input
                  type="password"
                  placeholder={config?.isConnected ? "••••••••••••••••••••" : "Paste your EAA... token"}
                  value={accessToken}
                  onChange={e => setAccessToken(e.target.value)}
                  required={!config?.isConnected}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                  Webhook Verification Token (Secret You Choose)
                </label>
                <input
                  type="text"
                  value={verifyToken}
                  onChange={e => setVerifyToken(e.target.value)}
                  required
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                  WhatsApp Business Account (WABA) ID (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. 1058434142144"
                  value={wabaId}
                  onChange={e => setWabaId(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}
                />
              </div>

              <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '11px', color: '#64748b' }}>
                <strong>Webhook Configuration for Meta Dashboard:</strong>
                <div style={{ marginTop: '4px', wordBreak: 'break-all' }}>
                  <strong>Webhook URL:</strong> <code>{getWebhookURL()}</code>
                </div>
                <div style={{ marginTop: '2px' }}>
                  <strong>Verify Token:</strong> <code>{verifyToken}</code>
                </div>
              </div>

              {settingsError && (
                <div style={{ color: '#ef4444', fontSize: '13px', fontWeight: '500' }}>
                  ⚠️ {settingsError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <button
                  type="submit"
                  disabled={settingsSaving}
                  style={{
                    flex: 1,
                    background: '#22c55e',
                    border: 'none',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(34,197,94,0.2)'
                  }}
                >
                  {settingsSaving ? 'Connecting...' : 'Save & Connect'}
                </button>
                {config?.isConnected && (
                  <button
                    type="button"
                    onClick={handleDisconnect}
                    style={{
                      background: '#ef4444',
                      border: 'none',
                      color: 'white',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Disconnect Account
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

const WhatsApp = () => {
  return (
    <WhatsAppSocketProvider>
      <MainLayout>
        <WhatsAppPageContent />
      </MainLayout>
    </WhatsAppSocketProvider>
  );
};

export default WhatsApp;
